import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
// import CheckIcon from '@material-ui/icons/Check';
import IgnoreIcon from '@material-ui/icons/VisibilityOff';
// import ReplayIcon from '@material-ui/icons/Replay';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { useIssueActions } from '../../hooks/useIssueActions';
import { useIssueComments } from '../../hooks/useIssueComments';
import { CommentsComponent } from './CommentsComponent';
import { StatusUpdateDialog } from './StatusUpdateDialog';
import { ExceptionEntry, BreadcrumbEntry, RequestEntry, IssueDetailData, IssueStatus } from '../../types';
import AddIcon from '@material-ui/icons/Add';
import { useCreateTask } from '../../hooks/useCreateTask';
import { useSev2ProjectEntity } from '../../hooks/useUptimeMonitorEntity';
import { useEntity } from '@backstage/plugin-catalog-react';
import { TaskCreationDialog } from './TaskCreationDialog';
import { alertApiRef, useApi } from "@backstage/core-plugin-api";
import { useGetErrorTrackingTask } from '../../hooks/useGetErrorTrackingTask';
import { useCreateErrorTrackingTask } from '../../hooks/useCreateErrorTrackingTask';
import { OpenInNewOutlined } from '@material-ui/icons';
import { AISuggestionSection } from './AISuggestionSection';

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    paddingRight: theme.spacing(1),
    position: 'relative',
  },
  statusChip: {
    fontWeight: 'bold',
    marginLeft: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  codeBlock: {
    backgroundColor: theme.palette.type === 'dark' ? '#2d3748' : '#f6f8fa',
    border: `1px solid ${theme.palette.type === 'dark' ? '#4a5568' : '#e1e4e8'}`,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1),
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    overflow: 'auto',
    maxHeight: 200,
    color: theme.palette.text.primary,
  },
  stackFrame: {
    border: `1px solid ${theme.palette.type === 'dark' ? '#4a5568' : '#e1e4e8'}`,
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.type === 'dark' ? '#2d3748' : '#ffffff',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  frameHeader: {
    backgroundColor: theme.palette.type === 'dark' ? '#1a202c' : '#f8f9fa',
    padding: theme.spacing(1),
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  frameContent: {
    padding: theme.spacing(1),
    fontFamily: 'monospace',
    fontSize: '0.875rem',
  },
  contextLine: {
    backgroundColor: theme.palette.type === 'dark' ? '#744210' : '#fff5b4',
    fontWeight: 'bold',
  },
  inAppFrame: {
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
  systemFrame: {
    opacity: 0.7,
  },
  actionButton: {
    marginRight: theme.spacing(1),
    '&:last-child': {
      marginRight: 0,
    },
  },
  tagChip: {
    margin: theme.spacing(0.25),
    fontSize: '0.75rem',
  },
  statsSection: {
    minWidth: 200,
    maxWidth: 220,
    maxHeight: '70vh',
    overflowY: 'auto',
    paddingRight: theme.spacing(0.5),
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.type === 'dark' ? '#2d3748' : '#f1f1f1',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.type === 'dark' ? '#4a5568' : '#c1c1c1',
      borderRadius: '3px',
      '&:hover': {
        background: theme.palette.type === 'dark' ? '#718096' : '#a8a8a8',
      },
    },
  },
  tabContent: {
    paddingTop: theme.spacing(2),
  },
  comingSoon: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  stickyTabs: {
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 1000,
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  stickySidebar: {
    position: 'sticky',
    top: theme.spacing(2),
    height: 'fit-content',
    maxHeight: 'calc(100vh - 200px)',
    overflowY: 'auto',
  },
  scrollableContent: {
    maxHeight: 'calc(100vh - 300px)',
    overflowY: 'auto',
  },
  flexWithGap: {
    display: 'flex',
    '& > *:first-child': {
      marginRight: theme.spacing(2),
    },
  },
  breadcrumbItem: {
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? '#4a5568' : '#e1e4e8'}`,
    backgroundColor: theme.palette.type === 'dark' ? '#2d3748' : '#ffffff',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  breadcrumbCategory: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
}));

type IssueDetailComponentProps = {
  data: IssueDetailData | undefined;
  isLoading: boolean;
  error: Error | undefined;
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
  host: string;
  org: string;
};

export const IssueDetailComponent = ({
  data,
  isLoading,
  error,
  open,
  onClose,
  onUpdate,
  host,
  org,
}: IssueDetailComponentProps) => {
  const { entity } = useEntity();
  const classes = useStyles();
  const [currentTab, setCurrentTab] = useState(0);
  const [statusUpdateDialog, setStatusUpdateDialog] = useState<{
    open: boolean;
    status: IssueStatus | null;
  }>({ open: false, status: null });

  const { 
    markAsResolvedWithComment, 
    markAsIgnoredWithComment, 
    markAsUnresolvedWithComment, 
    isUpdatingWithComment 
  } = useIssueActions();

  const { 
    comments, 
    refetch: refetchComments,
    addComment: addIssueComment 
  } = useIssueComments(
    data?.issue ? { host, id: data.issue.id } : null
  );

  const issue = data?.issue;
  const eventDetails = data?.eventDetails;

  const { createTask, isCreating } = useCreateTask();
  const sev2ProjectResult = useSev2ProjectEntity(entity);
  
  const getErrorTrackingTask = useGetErrorTrackingTask({
      errorEventId: eventDetails?.eventID || '',
  });

  const createErrorTrackingTask = useCreateErrorTrackingTask({onSuccess: () => getErrorTrackingTask.refetch()});

  const alertApi = useApi(alertApiRef);

  const [taskCreationDialog, setTaskCreationDialog] = useState<{
    open: boolean;
  }>({ open: false });

  const handleCommentUpdate = useCallback(() => {
    if (refetchComments) {
      refetchComments();
    }
  }, [refetchComments]);

  useEffect(() => {
    if (currentTab === 2 && refetchComments) {
      refetchComments();
    }
  }, [currentTab, refetchComments]);

  useEffect(() => {
    if (open && refetchComments) {
      refetchComments();
    }
  }, [open, refetchComments]);

  const handleStatusUpdateClick = (status: IssueStatus) => {
    setStatusUpdateDialog({ open: true, status });
  };

  const [commentRefreshTrigger, setCommentRefreshTrigger] = useState(0);

  const handleStatusUpdateConfirm = async (comment: string) => {
    if (!issue || !statusUpdateDialog.status) return;

    try {
      const issueId = parseInt(issue.id);
      switch (statusUpdateDialog.status) {
        case IssueStatus.RESOLVED:
          await markAsResolvedWithComment(host, org, issueId, comment);
          break;
        case IssueStatus.IGNORED:
          await markAsIgnoredWithComment(host, org, issueId, comment);
          break;
        case IssueStatus.UNRESOLVED:
          await markAsUnresolvedWithComment(host, org, issueId, comment);
          break;
      }
      
      setStatusUpdateDialog({ open: false, status: null });
      onUpdate();
      
      setCommentRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Failed to update issue status:', err);
    }
  };

  const handleStatusUpdateClose = () => {
    setStatusUpdateDialog({ open: false, status: null });
  };

  const handleCreateTaskClick = () => {
    if (!sev2ProjectResult.success || !sev2ProjectResult.data) {
      console.error('Sev2 project configuration error:', sev2ProjectResult.error);
      return;
    }
    
    setTaskCreationDialog({ open: true });
  };
  
  const handleTaskCreationClose = () => {
    setTaskCreationDialog({ open: false });
  };
  
  const handleTaskCreationConfirm = async (comment: string) => {
    if (!issue || !eventDetails) {
      console.error('Missing issue or event details for task creation');
      return;
    }
  
    if (!sev2ProjectResult.success || !sev2ProjectResult.data) {
      console.error('Sev2 project configuration error:', sev2ProjectResult.error);
      return;
    }
  
    console.log('Creating task for issue:', issue.id, 'in project:', sev2ProjectResult.data.projectId);
  
    // Map error level to priority
    const getPriority = (level: string) => {
      switch (level?.toLowerCase()) {
        case 'error':
          return 'high';
        case 'warning':
          return 'normal';
        default:
          return 'low';
      }
    };
  
    // Get stack trace from exception entry
    const getStackTrace = () => {
      if (!eventDetails?.entries) return 'No stack trace available';
      
      const exceptionEntry = eventDetails.entries.find(
        entry => entry.type === 'exception'
      ) as any;
      
      if (!exceptionEntry) return 'No stack trace available';
      
      return exceptionEntry.data.values.map((exception: any) => 
        `${exception.type}: ${exception.value}`
      ).join('\n');
    };
  
    const taskParams = {
      title: `[ERROR][ID:${issue.id}] ${issue.metadata.type}: ${issue.metadata.value}`,
      description: `**Error Details:**
- **Issue ID**: ${issue.id}
- **Level**: ${issue.level.toUpperCase()}
- **First Seen**: ${new Date(issue.firstSeen).toLocaleString()}
- **Last Seen**: ${new Date(issue.lastSeen).toLocaleString()}
- **Event Count**: ${parseInt(issue.count).toLocaleString()}
- **Project**: ${issue.project.name}

**Stack Trace:**
\`\`\`
${getStackTrace()}
\`\`\`

**Platform**: ${eventDetails.platform}
**Event ID**: ${eventDetails.eventID}

**Original Issue**: ${window.location.href}`,
      priority: getPriority(issue.level),
      project: sev2ProjectResult.data.projectId,
      // subtype: 'bug',
    };
  
    try {
      const result = await createTask(taskParams);
      
      // Close the dialog first
      setTaskCreationDialog({ open: false });
      
      // Get task details for comment
      const taskId = result?.result?.object?.id;
      if (taskId) {
        const sev2Host = 'https://refactory.sev-2.com';
        const taskUrl = `${sev2Host}/T${taskId}`;
        
        await createErrorTrackingTask.execute({
          errorEventId: eventDetails.eventID,
          sev2TaskId: taskId,
        })
        
        // Add user's comment with task creation info
        const taskCreationComment = `Task created: ${comment}\n\nPhabricator Task: T${taskId}\nTask URL: ${taskUrl}`;
        
        try {
          await addIssueComment(taskCreationComment);
          console.log('Task creation comment added successfully');
          
          // Trigger comment refresh
          setCommentRefreshTrigger(prev => prev + 1);
          
        } catch (commentErr) {
          console.error('Failed to add task creation comment:', commentErr);
        }
        
        alertApi.post({
          message: `Task successfully created with URL: ${taskUrl}`,
          severity: 'success',
        })
      }
    } catch (err) {
      console.error('Failed to create task:', err);
      setTaskCreationDialog({ open: false });
      
      alertApi.post({
        message: 'Failed to create task',
        severity: 'error',
      })
      
      // Add error comment
      try {
        const errorComment = `Failed to create task: ${comment}\n\nError: ${err instanceof Error ? err.message : 'Unknown error'}`;
        await addIssueComment(errorComment);
        
        // Trigger comment refresh
        setCommentRefreshTrigger(prev => prev + 1);
      } catch (commentErr) {
        console.error('Failed to add error comment:', commentErr);
      }
    }
  };

  const getStatusChip = (status: string) => {
    const statusColors = {
      resolved: { color: '#28a745', backgroundColor: '#d4edda' },
      unresolved: { color: '#dc3545', backgroundColor: '#f8d7da' },
      ignored: { color: '#6c757d', backgroundColor: '#f8f9fa' },
    };

    const style = statusColors[status as keyof typeof statusColors] || statusColors.unresolved;

    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        size="small"
        className={classes.statusChip}
        style={style}
      />
    );
  };

  const getRelativeTime = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds} seconds ago`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      return `${months} month${months !== 1 ? 's' : ''} ago`;
    } else {
      const years = Math.floor(diffInSeconds / 31536000);
      return `${years} year${years !== 1 ? 's' : ''} ago`;
    }
  };

  const renderStackTrace = () => {
    if (!eventDetails?.entries) return null;

    const exceptionEntry = eventDetails.entries.find(
      entry => entry.type === 'exception'
    ) as ExceptionEntry | undefined;
    
    if (!exceptionEntry) return null;

    return (
      <Box>
        <Typography className={classes.sectionTitle}>Stack Trace</Typography>
        {exceptionEntry.data.values.map((exception, index) => (
          <Box key={index} mb={2}>
            <Typography variant="h6" color="error" gutterBottom>
              {exception.type}: {exception.value}
            </Typography>
            {exception.stacktrace.frames.map((frame, frameIndex) => (
              <Accordion 
                key={frameIndex} 
                className={`${classes.stackFrame} ${frame.inApp ? classes.inAppFrame : classes.systemFrame}`}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box className={classes.frameHeader} width="100%">
                    <Typography variant="body2">
                      {frame.filename} in {frame.function} at line {frame.lineNo}
                      {frame.inApp && ' (Application Code)'}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box width="100%">
                    {frame.context && (
                      <Box className={classes.codeBlock}>
                        {frame.context.map(([lineNo, code]) => (
                          <Box 
                            key={lineNo} 
                            className={lineNo === frame.lineNo ? classes.contextLine : ''}
                          >
                            <Typography variant="body2" component="span">
                              {lineNo}: {code}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {frame.vars && Object.keys(frame.vars).length > 0 && (
                      <Box mt={1}>
                        <Typography variant="subtitle2" gutterBottom>Variables:</Typography>
                        <Table size="small">
                          <TableBody>
                            {Object.entries(frame.vars).map(([key, value]) => (
                              <TableRow key={key}>
                                <TableCell component="th" scope="row">{key}</TableCell>
                                <TableCell>{String(value)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  const renderBreadcrumbs = () => {
    if (!eventDetails?.entries) return null;

    const breadcrumbsEntry = eventDetails.entries.find(
      entry => entry.type === 'breadcrumbs'
    ) as BreadcrumbEntry | undefined;
    
    if (!breadcrumbsEntry) return null;

    return (
      <Box>
        <Typography className={classes.sectionTitle}>Breadcrumbs</Typography>
        <Box>
          {breadcrumbsEntry.data.values.map((breadcrumb, index) => (
            <Box key={index} className={classes.breadcrumbItem}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={10}>
                  <Typography variant="body2" className={classes.breadcrumbCategory}>
                    {breadcrumb.category}
                  </Typography>
                  <Typography variant="body2">{breadcrumb.message}</Typography>
                  {breadcrumb.data && (
                    <Typography variant="caption" color="textSecondary">
                      {JSON.stringify(breadcrumb.data, null, 2)}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="caption" color="textSecondary" align="right">
                    {new Date(breadcrumb.timestamp).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: false,
                      timeZoneName: 'longOffset'
                    })}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderRequestDetails = () => {
    if (!eventDetails?.entries) return null;

    const requestEntry = eventDetails.entries.find(
      entry => entry.type === 'request'
    ) as RequestEntry | undefined;
    
    if (!requestEntry) return null;

    return (
      <Box>
        <Typography className={classes.sectionTitle}>Request Details</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2"><strong>Method:</strong> {requestEntry.data.method}</Typography>
            <Typography variant="body2"><strong>URL:</strong> {requestEntry.data.url}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Headers:</Typography>
            <Table size="small">
              <TableBody>
                {requestEntry.data.headers.map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell component="th" scope="row">{key}</TableCell>
                    <TableCell>{value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderEnvironmentInfo = () => {
    if (!eventDetails?.contexts) return null;

    return (
      <Box>
        <Typography className={classes.sectionTitle}>Environment</Typography>
        <Grid container spacing={2}>
          {eventDetails.contexts.runtime && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2">Runtime</Typography>
                  <Typography variant="body2">
                    {eventDetails.contexts.runtime.name} {eventDetails.contexts.runtime.version}
                  </Typography>
                  <Typography variant="caption">SAPI: {eventDetails.contexts.runtime.sapi}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          {eventDetails.contexts.os && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2">Operating System</Typography>
                  <Typography variant="body2">
                    {eventDetails.contexts.os.name} {eventDetails.contexts.os.version}
                  </Typography>
                  <Typography variant="caption">{eventDetails.contexts.os.build}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          {eventDetails.contexts.trace && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2">Trace Information</Typography>
                  <Typography variant="body2">Trace ID: {eventDetails.contexts.trace.trace_id}</Typography>
                  <Typography variant="caption">Span ID: {eventDetails.contexts.trace.span_id}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  const renderEventDetailsTab = () => {
    const eventKey = eventDetails?.eventID || 'no-event';
    
    return (
      <Box className={classes.tabContent} key={eventKey}>
        {/* Basic Event Info */}
        <Box mb={3}>
          <Typography className={classes.sectionTitle}>Event Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Typography variant="body2"><strong>Event ID:</strong> {eventDetails?.eventID}</Typography>
              <Typography variant="body2"><strong>Platform:</strong> {eventDetails?.platform}</Typography>
              <Typography variant="body2">
                <strong>Timestamp:</strong> {eventDetails?.dateCreated && new Date(eventDetails.dateCreated).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: false,
                              timeZoneName: 'longOffset'
                            })}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider style={{ margin: '16px 0' }} />
  
        {/* AI Suggestion Section */}
        <AISuggestionSection 
          key={`ai-section-${eventKey}`}
          eventId={eventDetails?.eventID} 
          entries={eventDetails?.entries}
        />
  
        <Divider style={{ margin: '16px 0' }} />
  
        {renderStackTrace()}

        <Divider style={{ margin: '16px 0' }} />
  
        {/* Breadcrumbs */}
        {renderBreadcrumbs()}
  
        <Divider style={{ margin: '16px 0' }} />
  
        {/* Request Details */}
        {renderRequestDetails()}
  
        <Divider style={{ margin: '16px 0' }} />
  
        {/* Environment Info */}
        {renderEnvironmentInfo()}
  
        {/* Package Information */}
        {eventDetails?.packages && Object.keys(eventDetails.packages).length > 0 && (
          <>
            <Divider style={{ margin: '16px 0' }} />
            <Box>
              <Typography className={classes.sectionTitle}>Packages</Typography>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>View installed packages ({Object.keys(eventDetails.packages).length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableBody>
                      {Object.entries(eventDetails.packages).map(([pkg, version]) => (
                        <TableRow key={pkg}>
                          <TableCell component="th" scope="row">{pkg}</TableCell>
                          <TableCell>{version}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            </Box>
          </>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Box display="flex" alignItems="center" mb={0.5}>
              <Typography variant="h6" style={{ marginRight: 8 }}>
                {issue ? issue.metadata.type : 'Issue'}
              </Typography>
            </Box>
            <Typography variant="body2">
              {issue ? issue.metadata.value : ''}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="medium">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && <Progress />}
        {error && <ResponseErrorPanel error={error} />}
        
        {issue && (
          <Box>
            <Box className={classes.flexWithGap}>
              {/* Main Content */}
              <Box flex={1}>
                {/* Tabs */}
                <Box className={classes.stickyTabs}>
                  <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
                  <Tab label="Event Details" />
                      <Tab label="User Reports" />
                      <Tab label={
                        <Box display="flex" alignItems="center">
                        Comments
                        {Array.isArray(comments) && comments.length > 0 && (
                          <Chip 
                            label={comments.length} 
                            size="small" 
                            style={{ 
                              marginLeft: 8, 
                              backgroundColor: '#dc3545', 
                              color: 'white',
                              height: 20,
                              fontSize: '0.75rem'
                            }} 
                          />
                        )}
                      </Box>
                      } />
                  </Tabs>
                </Box>

                {/* Tab Content */}
                  <Box className={classes.scrollableContent}>
                    {currentTab === 0 && renderEventDetailsTab()}
                    {currentTab === 1 && (
                      <Box className={classes.comingSoon}>
                        <Typography variant="h6">Coming Soon</Typography>
                        <Typography variant="body2" color="textSecondary">
                          User reports functionality will be available in the next update.
                        </Typography>
                      </Box>
                    )}
                    {currentTab === 2 && (
                      <CommentsComponent 
                        host={host} 
                        issueId={issue.id} 
                        onCommentAdded={handleCommentUpdate}
                        refreshTrigger={commentRefreshTrigger}
                      />
                    )}
                  </Box>
              </Box>

              {/* Issue Stats Sidebar */}
              <Box className={`${classes.statsSection} ${classes.stickySidebar}`}>
                <Grid container spacing={1} direction="column">
                  <Grid item>
                    <Card variant="outlined">
                      <CardContent style={{ padding: 8 }}>
                        <Typography variant="caption" color="textSecondary">Issue #</Typography>
                        <Typography variant="body2"><strong>{issue.id}</strong></Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item>
                    <Card variant="outlined">
                      <CardContent style={{ padding: 8 }}>
                        <Typography variant="caption" color="textSecondary">Events</Typography>
                        <Typography variant="body2"><strong>{parseInt(issue.count).toLocaleString()}</strong></Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item>
                    <Card variant="outlined">
                      <CardContent style={{ padding: 8 }}>
                        <Typography variant="caption" color="textSecondary">Level</Typography>
                        <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                          <strong>{issue.level}</strong>
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item>
                    <Card variant="outlined">
                      <CardContent style={{ padding: 8 }}>
                        <Typography variant="caption" color="textSecondary">First Seen</Typography>
                        <Typography variant="body2" color="primary" style={{ marginBottom: 4 }}>
                          <strong>{getRelativeTime(issue.firstSeen)}</strong>
                        </Typography>
                        <Typography variant="body2">
                          <strong>
                            {new Date(issue.firstSeen).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: false,
                              timeZoneName: 'longOffset'
                            })}
                          </strong>
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item>
                    <Card variant="outlined">
                      <CardContent style={{ padding: 8 }}>
                        <Typography variant="caption" color="textSecondary">Last Seen</Typography>
                        <Typography variant="body2" color="primary" style={{ marginBottom: 4 }}>
                          <strong>{getRelativeTime(issue.lastSeen)}</strong>
                        </Typography>
                        <Typography variant="body2">
                          <strong>
                            {new Date(issue.lastSeen).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: false,
                              timeZoneName: 'longOffset'
                            })}
                          </strong>
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item>
                    <Card variant="outlined">
                      <CardContent style={{ padding: 8 }}>
                        <Typography variant="caption" color="textSecondary">Status</Typography>
                        <Box mt={0.5}>
                          {getStatusChip(issue.status)}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item>
                    <Card variant="outlined">
                      <CardContent style={{ padding: 8 }}>
                        <Typography variant="caption" color="textSecondary">Project</Typography>
                        <Typography variant="body2"><strong>{issue.project.name}</strong></Typography>
                        <Typography variant="caption">{issue.project.platform}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
          {issue && (
            <Box display="flex">
              {/* <Button
                className={classes.actionButton}
                startIcon={<CheckIcon />}
                onClick={() => handleStatusUpdateClick(IssueStatus.RESOLVED)}
                disabled={isUpdatingWithComment || issue.status === 'resolved'}
                variant="contained"
                color="primary"
                size="small"
              >
                Resolve
              </Button>
              <Button
                className={classes.actionButton}
                startIcon={<ReplayIcon />}
                onClick={() => handleStatusUpdateClick(IssueStatus.UNRESOLVED)}
                disabled={isUpdatingWithComment || issue.status === 'unresolved'}
                variant="outlined"
                size="small"
              >
                Reopen
              </Button> */}
              <Button
                className={classes.actionButton}
                startIcon={<IgnoreIcon />}
                onClick={() => handleStatusUpdateClick(IssueStatus.IGNORED)}
                disabled={isUpdatingWithComment || issue.status === 'ignored'}
                variant="outlined"
                size="small"
              >
                Ignore
              </Button>
              {getErrorTrackingTask.isLoading || isLoading ? (
                <>
                  <CircularProgress size={14} />
                </>
              ) : getErrorTrackingTask.data?.error_event_id !==
                eventDetails?.eventID ? (
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleCreateTaskClick}
                  disabled={
                    isCreating ||
                    !sev2ProjectResult.success ||
                    getErrorTrackingTask.isLoading ||
                    getErrorTrackingTask.data?.error_event_id ===
                      eventDetails?.eventID
                  }
                  variant="contained"
                  color="secondary"
                  size="small"
                >
                  {isCreating ? 'Creating Task...' : 'Create Task'}
                </Button>
              ) : (
                <Button
                  startIcon={<OpenInNewOutlined />}
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={() =>
                    window.open(
                      `https://refactory.sev-2.com/T${getErrorTrackingTask?.data?.task_id}`,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  Go to task
                </Button>
              )}
            </Box>
          )}
        </DialogActions>
        
        {/* Status Update Dialog */}
        <StatusUpdateDialog
          open={statusUpdateDialog.open}
          status={statusUpdateDialog.status}
          isUpdating={isUpdatingWithComment}
          onClose={handleStatusUpdateClose}
          onConfirm={handleStatusUpdateConfirm}
        />

        {/* Task Creation Dialog */}
        <TaskCreationDialog
          open={taskCreationDialog.open}
          isCreating={isCreating}
          onClose={handleTaskCreationClose}
          onConfirm={handleTaskCreationConfirm}
        />
    </Dialog>
  );
};
